import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { driverApi } from '../services/driverApi';

export const useGetVehicleMediaComments = (
  vehicleId: string | undefined,
  mediaUrl: string | undefined
) => {
  return useQuery({
    queryKey: ['vehicleMediaComments', vehicleId, mediaUrl],
    queryFn: () => driverApi.getVehicleMediaComments(vehicleId!, mediaUrl!),
    enabled: !!vehicleId && !!mediaUrl,
    staleTime: 30 * 1000,
  });
};

export const useAddVehicleMediaComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      vehicleId,
      mediaUrl,
      text,
    }: {
      vehicleId: string;
      mediaUrl: string;
      text: string;
      userName?: string;
    }) => driverApi.addVehicleMediaComment(vehicleId, { mediaUrl, text }),
    onMutate: async ({ vehicleId, mediaUrl, text, userName }) => {
      const queryKey = ['vehicleMediaComments', vehicleId, mediaUrl];
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);

      const optimistic = {
        id: `optimistic-${Date.now()}`,
        mediaUrl,
        vehicleId,
        userId: 'optimistic',
        text,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        user: { id: 'optimistic', name: userName || 'You' },
        _optimistic: true,
      };

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return { comments: [optimistic], total: 1 };
        return {
          ...old,
          comments: [...old.comments, optimistic],
          total: old.total + 1,
        };
      });

      return { previous, queryKey };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
    },
    onSettled: (_data, _err, { vehicleId, mediaUrl }) => {
      queryClient.invalidateQueries({
        queryKey: ['vehicleMediaComments', vehicleId, mediaUrl],
      });
    },
  });
};

export const useUpdateVehicleMediaComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, text }: { commentId: string; text: string }) =>
      driverApi.updateVehicleMediaComment(commentId, text),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['vehicleMediaComments', data?.vehicleId, data?.mediaUrl],
      });
    },
  });
};

export const useDeleteVehicleMediaComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: string) =>
      driverApi.deleteVehicleMediaComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicleMediaComments'] });
    },
  });
};
